/**
 * cart-upsell.js — Marmoset Found
 * Fixes applied:
 * 1. Shipping bar threshold corrected: 2000 → 20000 (cents)
 * 2. Double-init race condition fixed with _isAdding flag
 * 3. cart:update loop broken — skips re-init during active add
 * 4. Card removal guarded against detached DOM elements
 * 5. fetchCollectionFallback now receives params from init() safely
 */

class CartUpsell {
  constructor() {
    this.upsellSection = document.getElementById('mf-upsell-section');
    this.upsellContainer = document.getElementById('mf-upsell-products');
    this.giftWrapBtn = document.getElementById('mf-gift-wrap-btn');
    this.offerBanner = document.getElementById('mf-offer-banner');
    this._isAdding = false; // FIX 2: guard flag for active add operations

    if (this.upsellSection && this.upsellContainer) this.init();
    if (this.giftWrapBtn) this.initGiftWrap();
    this.initCloseBtn();
  }

  // ─── Close button ──────────────────────────────────────
  initCloseBtn() {
    const closeBtn = document.getElementById('mf-upsell-close');
    if (!closeBtn) return;
    closeBtn.addEventListener('click', () => {
      const section = document.getElementById('mf-upsell-section');
      if (!section) return;
      section.style.transition = 'opacity 0.3s';
      section.style.opacity = '0';
      setTimeout(() => section.remove(), 300);
    });
  }

  // ─── Main init ─────────────────────────────────────────
  async init() {
    const productId = this.upsellSection.dataset.productId;

    // FIX 5: capture dataset values now, while element is in DOM
    const collectionHandle = this.upsellSection.dataset.collectionHandle;
    const productType = this.upsellSection.dataset.productType;

    if (!productId) return;

    try {
      const [recoRes, cartRes] = await Promise.all([
        fetch(`/recommendations/products.json?product_id=${productId}&limit=8&intent=related`),
        fetch('/cart.js'),
      ]);

      const [recoData, cart] = await Promise.all([
        recoRes.json(),
        cartRes.json(),
      ]);

      const cartProductIds = cart.items.map(i => i.product_id);

      let products = (recoData.products || []).filter(
        p => !cartProductIds.includes(p.id) && p.available
      );

      // ── Fallback: pull from collection if API returned nothing ──
      if (products.length === 0) {
        // FIX 5: pass pre-captured values instead of re-querying DOM
        products = await this.fetchCollectionFallback(productId, cartProductIds, collectionHandle, productType);
      }

      if (products.length === 0) {
        this.upsellSection.style.display = 'none';
        return;
      }

      this.renderCards(products.slice(0, 4));
      this.initArrows();

    } catch (e) {
      console.warn('[MF] Upsell init failed:', e);
      this.upsellSection.style.display = 'none';
    }
  }

  // ─── Collection fallback ───────────────────────────────
  // FIX 5: accepts collectionHandle and productType as params
  async fetchCollectionFallback(productId, cartProductIds, collectionHandle, productType) {
    const urls = [
      collectionHandle && `/collections/${collectionHandle}/products.json?limit=10`,
      productType && `/collections/${productType}/products.json?limit=10`,
    ].filter(Boolean);

    for (const url of urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const data = await res.json();
        const filtered = (data.products || []).filter(
          p => p.id !== parseInt(productId) && !cartProductIds.includes(p.id) && p.available
        );
        if (filtered.length > 0) return filtered;
      } catch (e) {
        console.warn('[MF] Collection fallback failed for:', url, e);
      }
    }

    return [];
  }

  // ─── Render cards ──────────────────────────────────────
  renderCards(products) {
    this.upsellContainer.innerHTML = products.map(p => {
      const variant = p.variants.find(v => v.available) || p.variants[0];
      const isMultiVariant = p.variants.length > 1;
      const price = this.formatMoney(variant.price);
      const img = p.featured_image
        ? p.featured_image.replace(/\.(\w+)$/, '_280x280.$1')
        : '';

      return `
        <div class="mf-upsell-card">
          <a href="${p.url}" class="mf-upsell-card__img-wrap" tabindex="-1" aria-hidden="true">
            ${img ? `<img src="${img}" alt="${p.title}" loading="lazy" width="130" height="130">` : ''}
          </a>
          <p class="mf-upsell-card__title">${p.title}</p>
          <p class="mf-upsell-card__price">${price}</p>
          <button
            class="mf-upsell-card__btn"
            data-variant-id="${variant.id}"
            data-product-url="${p.url}"
            data-multi-variant="${isMultiVariant}"
            aria-label="${isMultiVariant ? 'View' : 'Add'} ${p.title}"
          >
            ${isMultiVariant ? 'Choose options' : 'Add to cart'}
          </button>
        </div>
      `;
    }).join('');

    this.upsellContainer.querySelectorAll('.mf-upsell-card__btn').forEach(btn => {
      btn.addEventListener('click', e => {
        if (btn.dataset.multiVariant === 'true') {
          window.location.href = btn.dataset.productUrl;
        } else {
          this.handleQuickAdd(e);
        }
      });
    });
  }

  // ─── Arrows ────────────────────────────────────────────
  initArrows() {
    const track = document.getElementById('mf-upsell-products');
    const left = document.getElementById('mf-arrow-left');
    const right = document.getElementById('mf-arrow-right');
    if (!track || !left || !right) return;
    left.addEventListener('click', () => track.scrollBy({ left: -145, behavior: 'smooth' }));
    right.addEventListener('click', () => track.scrollBy({ left: 145, behavior: 'smooth' }));
  }

  // ─── Quick add ─────────────────────────────────────────
  async handleQuickAdd(e) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('mf-upsell-card__btn--loading')) return;

    // FIX 2 & 3: signal that an add is in progress before any async work
    this._isAdding = true;

    btn.classList.add('mf-upsell-card__btn--loading');
    btn.textContent = '...';

    try {
      const sectionId = document.querySelector('cart-items-component[data-section-id]')?.dataset?.sectionId;
      const body = { id: variantId, quantity: 1 };
      if (sectionId) body.sections = sectionId;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Add to cart failed');
      const addedItem = await res.json();

      btn.classList.remove('mf-upsell-card__btn--loading');
      btn.classList.add('mf-upsell-card__btn--added');
      btn.textContent = 'Added ✓';

      await this.refreshCart(addedItem, variantId);

      // FIX 4: guard card removal against detached DOM elements
      setTimeout(() => {
        const card = btn.closest('.mf-upsell-card');
        if (card && card.isConnected) {
          card.style.transition = 'opacity 0.3s';
          card.style.opacity = '0';
          setTimeout(() => {
            if (card.isConnected) card.remove();
          }, 300);
        }
        // FIX 2: clear the adding flag after card is gone
        this._isAdding = false;
      }, 1200);

    } catch (e) {
      console.warn('[MF] Quick add failed:', e);
      btn.classList.remove('mf-upsell-card__btn--loading');
      btn.textContent = 'Add to cart';
      // FIX 2: always clear flag on failure too
      this._isAdding = false;
    }
  }

  // ─── Cart refresh ──────────────────────────────────────
  async refreshCart(addedItem, variantId) {
    try {
      const sectionId = document.querySelector('cart-items-component[data-section-id]')?.dataset?.sectionId;
      if (!sectionId) return;

      let sections = addedItem.sections;
      if (!sections) {
        const sectRes = await fetch(`/?sections=${sectionId}`);
        sections = await sectRes.json();
      }

      const cartEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: addedItem,
          sourceId: 'mf-upsell',
          data: {
            itemCount: addedItem.quantity,
            variantId: String(variantId),
            sections,
          },
        },
      });
      document.dispatchEvent(cartEvent);
    } catch (e) {
      console.warn('[MF] Cart refresh failed:', e);
    }
  }

  isFreeGiftItem(item) {
    if (item.properties) {
      for (const [key, val] of Object.entries(item.properties)) {
        const lowerKey = key.toLowerCase();
        const lowerVal = String(val).toLowerCase();
        if (
          lowerKey.includes('gift') || 
          lowerKey.includes('bogos') || 
          lowerKey.includes('vcdm') ||
          lowerVal.includes('gift') || 
          lowerVal.includes('bogos') || 
          lowerVal.includes('vcdm')
        ) {
          return true;
        }
      }
    }
    if (item.discount_allocations && item.discount_allocations.length > 0) {
      for (const discount of item.discount_allocations) {
        const title = (discount.title || '').toLowerCase();
        if (title.includes('gift') || title.includes('bogos') || title.includes('free') || title.includes('vcdm')) {
          return true;
        }
      }
    }
    if (item.product_type) {
      const pt = item.product_type.toLowerCase();
      if (pt.includes('gift') || pt.includes('vcdm')) {
        return true;
      }
    }
    if (Number(item.price) === 0 || Number(item.final_price) === 0 || Number(item.line_price) === 0) {
      return true;
    }
    const title = (item.title || '').toLowerCase();
    const handle = (item.handle || '').toLowerCase();
    const productTitle = (item.product_title || '').toLowerCase();
    if (
      title.includes('gift') || 
      title.includes('vcdm') || 
      handle.includes('gift') || 
      handle.includes('vcdm') || 
      productTitle.includes('gift') || 
      productTitle.includes('vcdm')
    ) {
      return true;
    }
    return false;
  }

  // ─── Shipping bar refresh ──────────────────────────────
  async refreshShippingBar() {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      const rate = parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || 1);
      const thresholdAUD = 22000;
      let cartTotal = 0;
      if (cart.items && cart.items.length) {
        for (const item of cart.items) {
          if (!this.isFreeGiftItem(item)) {
            cartTotal += item.final_line_price;
          }
        }
      }
      const cartAUD = Math.round(cartTotal / rate);
      const remainingAUD = Math.max(0, thresholdAUD - cartAUD);
      const remainingPresentment = Math.round(remainingAUD * rate);
      const pct = Math.min(100, Math.round((cartAUD / thresholdAUD) * 100));

      const fill = document.querySelector('.mf-shipping-bar__fill');
      const msg = document.querySelector('.mf-shipping-bar__message');

      if (fill) fill.style.width = pct + '%';
      if (msg) {
        if (cartAUD >= thresholdAUD) {
          fill?.classList.add('mf-shipping-bar__fill--complete');
          msg.innerHTML = '<span class="mf-shipping-bar__icon">✦</span> You\'ve unlocked <strong>free shipping</strong> — enjoy!';
        } else {
          msg.innerHTML = `Add <strong>${this.formatMoney(remainingPresentment)}</strong> more for free shipping`;
        }
      }
    } catch (e) {
      console.warn('[MF] Shipping bar refresh failed:', e);
    }
  }

  // ─── Gift wrap ─────────────────────────────────────────
  initGiftWrap() {
    this.giftWrapBtn.addEventListener('click', async () => {
      const variantId = this.giftWrapBtn.dataset.variantId;
      if (!variantId) return;

      this.giftWrapBtn.textContent = '...';
      this.giftWrapBtn.disabled = true;

      try {
        const sectionId = document.querySelector('cart-items-component[data-section-id]')?.dataset?.sectionId;
        const body = { id: variantId, quantity: 1 };
        if (sectionId) body.sections = sectionId;

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const addedItem = await res.json();

        this.giftWrapBtn.textContent = 'Added ✓';
        this.giftWrapBtn.classList.add('mf-offer-banner__btn--added');
        await this.refreshCart(addedItem, variantId);

        setTimeout(() => {
          if (this.offerBanner && this.offerBanner.isConnected) {
            this.offerBanner.style.transition = 'opacity 0.4s';
            this.offerBanner.style.opacity = '0';
            setTimeout(() => {
              if (this.offerBanner.isConnected) this.offerBanner.remove();
            }, 400);
          }
        }, 1500);

      } catch (e) {
        console.warn('[MF] Gift wrap add failed:', e);
        this.giftWrapBtn.textContent = 'Add $5';
        this.giftWrapBtn.disabled = false;
      }
    });
  }

  // ─── Format money ──────────────────────────────────────
  formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return new Intl.NumberFormat(navigator.language || 'en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(cents / 100);
  }
}

// ─── Payment button styling ────────────────────────────
function stylePaymentButtons() {
  const ctas = document.querySelector('.cart__ctas');
  if (!ctas) return;
  ctas.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:6px;';
  const checkout = ctas.querySelector('.cart__checkout-button');
  if (checkout) checkout.style.cssText = 'grid-column:1/-1;height:40px;font-size:12px;';
  const additional = ctas.querySelector('.cart__additional-checkout-buttons, .additional-checkout-buttons');
  if (additional) {
    additional.style.cssText = 'display:contents;';
    additional.querySelectorAll(':scope > *').forEach(btn => {
      btn.style.cssText = 'width:100%;min-width:0;height:36px;';
    });
  }
}

// ─── Reliable init via MutationObserver ───────────────
let upsellInitialised = false;
let activeInstance = null; // FIX 3: track the active instance

function tryInitUpsell() {
  const section = document.getElementById('mf-upsell-section');
  const container = document.getElementById('mf-upsell-products');
  if (!section || !container) return;

  // Only run if skeletons are still present (not yet loaded)
  if (!container.querySelector('.mf-upsell__skeleton')) return;

  // Guard against double-init within the same render cycle
  if (upsellInitialised) return;
  upsellInitialised = true;

  activeInstance = new CartUpsell();

  // Reset flag after a tick so future re-renders can re-init
  setTimeout(() => { upsellInitialised = false; }, 1000);
}

// Watch for the upsell section being injected or re-injected
const upsellObserver = new MutationObserver(() => {
  tryInitUpsell();
});

upsellObserver.observe(document.body, {
  childList: true,
  subtree: true,
});

// Fallback listeners
document.addEventListener('DOMContentLoaded', () => {
  tryInitUpsell();
  setTimeout(stylePaymentButtons, 800);
});

document.addEventListener('cart:update', () => {
  // FIX 3: skip re-init entirely if an add is currently in progress
  if (activeInstance?._isAdding) return;

  // Reset so next render triggers a fresh init
  upsellInitialised = false;
  setTimeout(() => {
    tryInitUpsell();
    stylePaymentButtons();
  }, 800);
});